import { useSyncFilterPanelPositionWhileOpen } from "@lhd/components/FilterIcon/filterPanelAnchor";
import { BsSearch } from "react-icons/bs";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Checkbox, Form, Input, Radio, Spin } from "antd";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import ReactDOM from "react-dom";
import { Styles } from "./styled";
import { isEmpty, minBy } from "lodash";
import Button from "@lhd/components/Button";
import DateHierarchyFilter, {
  formatRawDateToAPI,
} from "@lhd/components/DateTree";
import { isNullFilterToken } from "@lhd/utility";
import { IPropsFilterPanel } from "../interface";
import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";

const Date_Tree = [
  APP_PART_TYPES.CREATED_DATETIME,
  APP_PART_TYPES.UPDATED_DATETIME,
  APP_PART_TYPES.DATE,
  APP_PART_TYPES.DATE_AND_TIME,
  APP_PART_TYPES.SIGNED_DATE,
] as string[];

const sortForPanelDisplay = (sort: unknown): "" | "asc" | "desc" => {
  if (sort === "asc" || sort === "desc") return sort;
  return "";
};

const sortFromFilterState = (sort: unknown): "" | "asc" | "desc" => {
  if (sort === "asc" || sort === "desc") return sort;
  return "";
};

const sortForSave = (
  localSort: unknown,
  persistedSort: unknown,
): "" | "asc" | "desc" | undefined => {
  if (localSort === "asc" || localSort === "desc") return localSort;
  if (localSort === "") {
    if (persistedSort === "asc" || persistedSort === "desc") return "";
    return undefined;
  }
  return undefined;
};

function assignForwardedRef<T>(
  ref: React.Ref<T> | undefined,
  value: T | null,
): void {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref && typeof ref === "object" && "current" in ref) {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

const Panel = (props: IPropsFilterPanel, ref: React.Ref<HTMLDivElement>) => {
  const {
    type,
    position,
    columnId,
    handleSetFilter,
    setFilterInfo,
    filter,
    filterDataFull,
    open,
    filterData,
    isLoadingMoreOptions,
    isFetchingOptions,
    options,
    hideSpecialEmptyCheckbox,
    enableTimeTree,
  } = props;
  const isHierarchyTree =
    Date_Tree.includes(type || "") ||
    (enableTimeTree === true && type === APP_PART_TYPES.TIME);
  const [form] = Form.useForm();
  const [localFilter, setLocalFilter] = useState<Record<string, any>>({});
  const [panelPosition, setPanelPosition] = useState(position);
  const orderMainFilterRef = useRef<Record<string, number>>({});
  const panelRootRef = useRef<HTMLDivElement | null>(null);

  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRootRef.current = node;
      assignForwardedRef(ref, node);
    },
    [ref],
  );

  const filterOptions = useMemo(() => {
    if (options) return options;
    const minKey = minBy(
      Object.keys(orderMainFilterRef.current).filter(
        (k) => orderMainFilterRef.current[k] !== 0,
      ),
      (k) => orderMainFilterRef.current[k],
    );

    const data =
      (minKey === columnId ? filterDataFull || {} : filterData || {})[
        columnId
      ] || [];
    const dataSet = new Set(data);
    const dataClear = Array.from(dataSet);
    return dataClear.map((x) => ({
      label: x,
      value: x,
    }));
  }, [filterData, columnId, filterDataFull, options]);

  const optionFilterCellEmpty = {
    label: "(空白)",
    value: "special_empty_",
  };

  const blankFilterOptions = useMemo(
    () => filterOptions.filter((item) => isNullFilterToken(String(item.value))),
    [filterOptions],
  );
  const dateHierarchyOptions = useMemo(
    () =>
      filterOptions.filter((item) => !isNullFilterToken(String(item.value))),
    [filterOptions],
  );

  const selectedNullTokenFilters = useMemo(
    () =>
      (localFilter.filter || [])
        .map(String)
        .filter((key: string) => isNullFilterToken(key)),
    [localFilter.filter],
  );
  const selectedDateHierarchyFilters = useMemo(
    () =>
      (localFilter.filter || [])
        .map(String)
        .filter((key: string) => !isNullFilterToken(key)),
    [localFilter.filter],
  );

  const applyFilterSelection = useCallback(
    (nextFilter: (string | number)[]) => {
      const normalizedFilter = nextFilter.map(String);
      form.setFieldValue("filter", normalizedFilter);
      setLocalFilter((prev) => ({
        ...prev,
        filter: normalizedFilter,
      }));
    },
    [form],
  );

  const handleBlankFilterChange = useCallback(
    (values: CheckboxValueType[]) => {
      applyFilterSelection([
        ...selectedDateHierarchyFilters,
        ...values.map(String),
      ]);
    },
    [applyFilterSelection, selectedDateHierarchyFilters],
  );

  const handleDateHierarchyFilterChange = useCallback(
    (values: string[], _orig?: string[], apiKeys?: string[]) => {
      applyFilterSelection([
        ...selectedNullTokenFilters,
        ...(apiKeys || values),
      ]);
    },
    [applyFilterSelection, selectedNullTokenFilters],
  );

  const handleTextFilterChange = useCallback(
    (values: CheckboxValueType[]) => {
      applyFilterSelection([
        ...selectedNullTokenFilters,
        ...values.map(String),
      ]);
    },
    [applyFilterSelection, selectedNullTokenFilters],
  );

  useEffect(() => {
    const filterItem = (filter || {})[columnId] || {};
    const initialValues = {
      ...filterItem,
      sort: sortFromFilterState(filterItem.sort),
      filter: Object.keys((filterItem as any)?.filter || {}),
    };

    if (!filterItem.filter && filterOptions.length > 0) {
      initialValues.filter = filterOptions.map((item) =>
        isHierarchyTree
          ? formatRawDateToAPI(item.value as string, type) || item.value
          : String(item.value),
      );
    }
    if (!("filter_special_empty" in filterItem)) {
      initialValues.filter_special_empty = !hideSpecialEmptyCheckbox;
    }

    setLocalFilter(initialValues);
    form.setFieldsValue(initialValues);
  }, [filter, columnId, form]);

  const isDirtyRef = useRef(false);
  const autoFilledColumnId = useRef<any>(null);

  useEffect(() => {
    isDirtyRef.current = false;
  }, [columnId]);

  useEffect(() => {
    const filterItem = (filter || {})[columnId] || {};
    if (
      !filterItem.filter &&
      filterOptions.length > 0 &&
      autoFilledColumnId.current !== columnId &&
      !isDirtyRef.current
    ) {
      const all = filterOptions.map((item) =>
        isHierarchyTree
          ? formatRawDateToAPI(item.value as string, type) || item.value
          : String(item.value),
      );
      setLocalFilter((prev) => {
        const next: Record<string, any> = { ...prev, filter: all };
        if (!("filter_special_empty" in filterItem)) {
          next.filter_special_empty = !hideSpecialEmptyCheckbox;
        }
        form.setFieldsValue(next);
        return next;
      });
      autoFilledColumnId.current = columnId;
    }
  }, [filterOptions, filter, columnId, hideSpecialEmptyCheckbox, form]);

  useEffect(() => {
    if (!open) {
      autoFilledColumnId.current = null;
      return;
    }
    setPanelPosition(position);
  }, [open, position]);

  useEffect(() => {
    if (!open || !panelRootRef.current) return;
    const panelRect = panelRootRef.current.getBoundingClientRect();
    const margin = 8;

    const nextTop = Math.min(
      Math.max(margin, panelPosition.y),
      window.innerHeight - panelRect.height - margin,
    );
    const nextLeft = Math.min(
      Math.max(margin, panelPosition.x),
      window.innerWidth - panelRect.width - margin,
    );

    if (nextTop !== panelPosition.y || nextLeft !== panelPosition.x) {
      setPanelPosition({ x: nextLeft, y: nextTop });
    }
  }, [open, filterOptions, localFilter, panelPosition]);

  useSyncFilterPanelPositionWhileOpen({
    open: !!open,
    columnId,
    setFilterInfo,
  });

  /** Khi nhấn 保存 */
  const handleFinish = () => {
    const filterItem = (filter || {})[columnId] || {};
    const filterAsObj = (localFilter.filter || []).reduce(
      (acc: any, k: string) => ((acc[k] = true), acc),
      {},
    );
    const isCheckAll =
      (localFilter.filter || []).length === filterOptions.length &&
      (hideSpecialEmptyCheckbox || localFilter.filter_special_empty !== false);

    const newFilter: any = {
      ...localFilter,
      sort: sortForSave(localFilter.sort, filterItem.sort),
      filter: filterAsObj,
      filterOptions,
    };

    if (isCheckAll) {
      delete newFilter.filter;
      delete newFilter.filter_special_empty;
      newFilter.isCheckAll = true;
    } else {
      delete newFilter.isCheckAll;
    }

    handleSetFilter({ [columnId]: newFilter }, options || []);

    const isEmptyOption = isEmpty(localFilter.filter);
    orderMainFilterRef.current = {
      ...orderMainFilterRef.current,
      [columnId]: isEmptyOption
        ? 0
        : orderMainFilterRef.current[columnId] || Date.now(),
    };
    setFilterInfo((prev: any) => ({
      ...prev,
      open: false,
      position: { x: 0, y: 0 },
      columnId: "",
    }));
  };

  const handleCancel = () => {
    setFilterInfo((prev: any) => ({
      ...prev,
      open: false,
      position: { x: 0, y: 0 },
      columnId: "",
    }));
  };

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (e: MouseEvent) => {
      const root = panelRootRef.current;
      if (!root || root.contains(e.target as Node)) return;
      setFilterInfo((prev: any) => ({
        ...prev,
        open: false,
        position: { x: 0, y: 0 },
        columnId: "",
      }));
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open, setFilterInfo]);

  return ReactDOM.createPortal(
    <Styles
      ref={setPanelRef}
      className={`filter_container ${open ? "open" : "close"}`}
      style={{
        position: "fixed",
        background: "#fff",
        left: `${panelPosition.x}px`,
        top: `${panelPosition.y}px`,
        zIndex: 1001,
      }}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        onValuesChange={() => {
          isDirtyRef.current = true;
        }}
      >
        <div className="filter-wrapper">
          {/* Sort */}
          <div className="sort">
            <Form.Item label="ソート" name="sort">
              <Radio.Group
                value={sortForPanelDisplay(localFilter.sort)}
                onChange={(e) => {
                  const nextSort = sortForPanelDisplay(e.target.value);
                  form.setFieldValue("sort", nextSort);
                  setLocalFilter((prev) => ({ ...prev, sort: nextSort }));
                }}
                options={[
                  { label: "なし", value: "" },
                  { label: "昇順 (A->Z)", value: "asc" },
                  { label: "降順 (Z->A)", value: "desc" },
                ]}
              />
            </Form.Item>
          </div>

          {/* Search */}
          <div className="search">
            <p className="title">検索</p>
            <Form.Item name="search">
              <Input
                suffix={<BsSearch />}
                value={localFilter.search || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  form.setFieldValue("search", value);
                  setLocalFilter((prev) => ({ ...prev, search: value }));
                }}
                placeholder=""
              />
            </Form.Item>
          </div>
          <Spin spinning={!!isFetchingOptions || !!isLoadingMoreOptions}>
            {/* Filter */}
            <div className="filter">
              <p className="title">フィルター</p>
              <div
                className="filter-options"
                style={{
                  flexDirection: "column",
                  maxHeight: "calc(100vh - 660px)",
                  minHeight: 100,
                }}
              >
                {!hideSpecialEmptyCheckbox && (
                  <Form.Item
                    valuePropName="checked"
                    name="filter_special_empty"
                    className="filter_special_empty"
                  >
                    <Checkbox
                      checked={localFilter.filter_special_empty || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        form.setFieldValue("filter_special_empty", checked);
                        setLocalFilter((prev) => ({
                          ...prev,
                          filter_special_empty: checked,
                        }));
                      }}
                    >
                      {optionFilterCellEmpty.label}
                    </Checkbox>
                  </Form.Item>
                )}

                {hideSpecialEmptyCheckbox && blankFilterOptions.length > 0 && (
                  <Checkbox.Group
                    value={selectedNullTokenFilters}
                    onChange={handleBlankFilterChange}
                    style={{ width: "100%" }}
                  >
                    {blankFilterOptions.map((item, idx) => (
                      <div
                        key={`blank-${String(item.value)}-${idx}`}
                        style={{ width: "228px" }}
                      >
                        <Checkbox value={String(item.value)}>
                          {item.label}
                        </Checkbox>
                      </div>
                    ))}
                  </Checkbox.Group>
                )}

                <Form.Item name="filter">
                  {isHierarchyTree && !isEmpty(dateHierarchyOptions) ? (
                    <DateHierarchyFilter
                      mode={type}
                      parseClockTime={enableTimeTree === true}
                      timestamps={dateHierarchyOptions.map((item) => {
                        const timestamp =
                          formatRawDateToAPI(item.value as string, type) ||
                          (item.value as string);
                        const fixedTimestamp = timestamp.replace(
                          /日(\d{2}:\d{2})/,
                          "日 $1",
                        );
                        return fixedTimestamp;
                      })}
                      initialChecked={selectedDateHierarchyFilters}
                      onChange={handleDateHierarchyFilterChange}
                    />
                  ) : !isHierarchyTree && !isEmpty(dateHierarchyOptions) ? (
                    <Checkbox.Group
                      value={selectedDateHierarchyFilters}
                      onChange={handleTextFilterChange}
                      style={{ width: "100%" }}
                    >
                      {dateHierarchyOptions.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            width: "228px",
                          }}
                        >
                          <Checkbox value={String(item.value)}>
                            {item.label}
                          </Checkbox>
                        </div>
                      ))}
                    </Checkbox.Group>
                  ) : blankFilterOptions.length > 0 ||
                    !hideSpecialEmptyCheckbox ? null : (
                    <p
                      className="title"
                      style={{
                        opacity: 0.5,
                      }}
                    >
                      空
                    </p>
                  )}
                </Form.Item>
              </div>
            </div>
          </Spin>

          {/* Action buttons */}
          <div className="action">
            <p
              onClick={() => {
                const all = filterOptions.map((item) => {
                  const val = item.value;
                  if (isHierarchyTree) {
                    return formatRawDateToAPI(val as string, type) || val;
                  }
                  return String(val);
                });
                form.setFieldValue("filter", all);
                if (!hideSpecialEmptyCheckbox) {
                  form.setFieldValue("filter_special_empty", true);
                }
                setLocalFilter((prev) => ({
                  ...prev,
                  filter: all,
                  ...(hideSpecialEmptyCheckbox
                    ? {}
                    : { filter_special_empty: true }),
                }));
              }}
            >
              すべて選択
            </p>
            <span>-</span>
            <p
              onClick={() => {
                form.setFieldValue("filter", []);
                if (!hideSpecialEmptyCheckbox) {
                  form.setFieldValue("filter_special_empty", false);
                }
                setLocalFilter((prev) => ({
                  ...prev,
                  filter: [],
                  ...(hideSpecialEmptyCheckbox
                    ? {}
                    : { filter_special_empty: false }),
                }));
              }}
            >
              クリア
            </p>
          </div>
          <div className="footer">
            <Button
              className="btn btn-filter"
              label="保存"
              onClick={form.submit}
            />

            <Button
              className="btn btn-cancel"
              type="cancel-btn"
              label="キャンセル"
              onClick={handleCancel}
            />
          </div>
        </div>
      </Form>
    </Styles>,
    document.body,
  );
};

export default forwardRef(Panel);
